import {useEffect, useState} from 'react';

import {Stripe, StripeError} from '@stripe/stripe-js';
import Cookies from 'js-cookie';

import {stripeAmountToCurrencyAmount} from '@stryd/util-lib';

import buildInfo from 'src/cached-data/build-info.json';
import {usePageRegionData} from 'src/contexts';
import {useApi} from 'src/contexts/ApiContext';
import {useCart} from 'src/features/cart/contexts';
import {getReferrerId} from 'src/features/cart/helpers';
import {CartTestType} from 'src/features/cart/types';
import Bugsnag from 'src/utils/analytics/bugsnag';
import {beginCheckoutEvent, GaEcommerceItem} from 'src/utils/analytics/events';
import {GA_CLIENT_ID_COOKIE_NAME, track} from 'src/utils/analytics/gtag';
import {variantToItems} from 'src/utils/analytics/variantToItems';
import {getStripe} from 'src/utils/stripe/getStripe';

const {buildDate} = buildInfo;

type CheckoutErrorCode =
  | 'missing_key_error'
  | 'stryd_api_error'
  | 'stripe_js_load_error'
  | 'stripe_redirect_error';

interface ICheckoutError {
  code: CheckoutErrorCode;
  err?: any;
}

interface StripeMissingKeyCheckoutError extends ICheckoutError {
  code: 'missing_key_error';
}

interface StrydApiCheckoutError extends ICheckoutError {
  code: 'stryd_api_error';
  err: NonNullable<any>;
}

interface StripeJsLoadCheckoutError extends ICheckoutError {
  code: 'stripe_js_load_error';
}

interface StripeRedirectCheckoutError extends ICheckoutError {
  code: 'stripe_redirect_error';
  err: StripeError;
}

type CheckoutError =
  | StripeMissingKeyCheckoutError
  | StripeJsLoadCheckoutError
  | StripeRedirectCheckoutError
  | StrydApiCheckoutError;

export const useStripeCheckout = () => {
  const api = useApi();
  const {region, locale} = usePageRegionData();
  const [cart, cartDispatch] = useCart();
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useEffect(() => {
    getStripe().then((result) => {
      setStripe(result);
    });
  }, []);

  const handleCheckoutError = (
    error: CheckoutError
  ): {error: CheckoutError} => {
    return {error};
  };

  /**
   * Initializes a checkout session, and then redirects to Stripe Checkout
   *
   * If there is a subscription product in the cart, but no user email provided,
   * the user is redirected to the sign in screen.
   */
  const checkoutWithStripe = async (
    userEmail?: string
  ): Promise<void | {error: CheckoutError}> => {
    track(
      beginCheckoutEvent({
        ecommerce: {
          items: cart.lineItems.reduce((gaItems, lineItem) => {
            return [...gaItems, ...variantToItems(lineItem.selectedVariant)];
          }, [] as GaEcommerceItem[]),
          coupon: '',
          currency: cart.subtotal.currency,
          value: stripeAmountToCurrencyAmount(
            cart.subtotal.unit_amount,
            cart.subtotal.currency
          ),
        },
        includesSubscription:
          cart.hasSubscriptionProduct ||
          cart.cartTestType === CartTestType.FreeTrial,
        includesGift: !!cart.gift,
      })
    );

    if (!stripe) {
      Bugsnag.notify(
        new Error(`Tried to checkout with Stripe, but stripe-js was not loaded`)
      );
      return handleCheckoutError({code: 'stripe_js_load_error'});
    }

    const sessionResponse = await api.checkout.createSession(
      {
        region,
        locale,
        user_email: userEmail,
        updated_at: buildDate,
        test_type: cart.cartTestType || undefined,
        line_items: cart.lineItems.map((item) => {
          const {quantity, selectedVariant, product} = item;
          const stripeProductIds = selectedVariant.fields.stripeProducts.map(
            (product) => product.fields.stripeProductId
          );

          return {
            quantity,
            product_id: product.sys.id,
            selected_variant_id: selectedVariant.sys.id,
            selected_stripe_product_ids: stripeProductIds,
          };
        }),
        metadata: {
          ga_client_id: Cookies.get(GA_CLIENT_ID_COOKIE_NAME),
          has_ga: String(typeof window.ga === 'function'),
          ga_loaded: window.ga && window.ga.loaded ? 'true' : 'false',
        },
        // eslint-disable-next-line
        // @ts-ignore
        referrer_id: getReferrerId(),
        // eslint-disable-next-line
        // @ts-ignore
        gift: cart.gift,
      },
      {withCredentials: true}
    );

    if (!sessionResponse.ok) {
      Bugsnag.notify(
        new Error(
          `Checkout session creation error. Error: ${sessionResponse.err.response?.data}`
        )
      );

      return handleCheckoutError({
        code: 'stryd_api_error',
        err: sessionResponse.err,
      });
    }

    const {id: checkoutSessionId} = sessionResponse.data;

    cartDispatch({
      type: 'SET_CHECKOUT_SESSION_ID',
      region,
      checkoutSessionId,
    });

    const {error} = await stripe.redirectToCheckout({
      sessionId: checkoutSessionId,
    });

    if (error) {
      Bugsnag.notify(
        new Error(
          `Stripe redirect error. Error type: ${error.type}. Error code: ${error.code}`
        )
      );

      return handleCheckoutError({code: 'stripe_redirect_error', err: error});
    }
  };

  return {checkoutWithStripe, stripeLoaded: !!stripe};
};
